import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Send, User, Users, LogIn, LogOut, Smile, Mic, Radio, Bell, BellOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const EMOJIS = ['🔥', '❤️', '👏', '🎵', '🎤', '💯', '🙌', '😎', '🎧', '✨'];
const DJ_ACTIVE_MINUTES = 5; // DJ considered "active" if chatted within this time

// Create notification sound using Web Audio API
const createNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playChime = () => {
      // Create a pleasant two-tone chime
      const now = audioContext.currentTime;
      
      // First tone (higher)
      const osc1 = audioContext.createOscillator();
      const gain1 = audioContext.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now); // A5
      gain1.gain.setValueAtTime(0.3, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc1.connect(gain1);
      gain1.connect(audioContext.destination);
      osc1.start(now);
      osc1.stop(now + 0.3);
      
      // Second tone (lower, slightly delayed)
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1100, now + 0.1); // C#6
      gain2.gain.setValueAtTime(0, now);
      gain2.gain.setValueAtTime(0.25, now + 0.1);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.start(now + 0.1);
      osc2.stop(now + 0.5);
    };
    
    return { play: playChime, context: audioContext };
  } catch (e) {
    console.log('Web Audio API not supported');
    return null;
  }
};

export function Chat() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [connected, setConnected] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [djNotifications, setDjNotifications] = useState(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('djNotifications');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Initialize notification sound
  useEffect(() => {
    notificationSoundRef.current = createNotificationSound();
    return () => {
      if (notificationSoundRef.current?.context) {
        notificationSoundRef.current.context.close();
      }
    };
  }, []);

  // Save notification preference
  useEffect(() => {
    localStorage.setItem('djNotifications', JSON.stringify(djNotifications));
  }, [djNotifications]);

  // Play notification for DJ messages
  const playDJNotification = useCallback(() => {
    if (djNotifications && notificationSoundRef.current) {
      // Resume audio context if suspended (browser autoplay policy)
      if (notificationSoundRef.current.context.state === 'suspended') {
        notificationSoundRef.current.context.resume();
      }
      notificationSoundRef.current.play();
    }
  }, [djNotifications]);

  // Update current time every minute to refresh DJ active status
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Check if DJ/Admin/Mod has been active recently
  const djStatus = useMemo(() => {
    const now = currentTime;
    const activeThreshold = DJ_ACTIVE_MINUTES * 60 * 1000;
    
    // Find most recent DJ/admin/mod message
    const specialRoles = ['dj', 'admin', 'mod'];
    const recentSpecialMessage = [...messages].reverse().find(msg => 
      specialRoles.includes(msg.user_role)
    );
    
    if (!recentSpecialMessage) return null;
    
    const msgTime = new Date(recentSpecialMessage.timestamp).getTime();
    const timeDiff = now - msgTime;
    
    if (timeDiff <= activeThreshold) {
      return {
        name: recentSpecialMessage.user_name,
        role: recentSpecialMessage.user_role,
        isActive: true
      };
    }
    return null;
  }, [messages, currentTime]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch initial messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/chat/messages`);
        if (response.ok) {
          const data = await response.json();
          setMessages(data);
        }
      } catch (error) {
        console.log('Failed to fetch messages');
      }
    };
    fetchMessages();
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!user) return;

    const connectWS = () => {
      try {
        wsRef.current = new WebSocket(`${WS_URL}/ws/chat`);
        
        wsRef.current.onopen = () => {
          setConnected(true);
          // Send join message
          wsRef.current.send(JSON.stringify({
            type: 'join',
            user: { user_id: user.user_id, name: user.name, role: user.role }
          }));
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'message') {
            setMessages(prev => [...prev, data.data]);
            // Play notification for DJ/Admin/Mod messages (not own messages)
            const specialRoles = ['dj', 'admin', 'mod'];
            if (specialRoles.includes(data.data.user_role) && data.data.user_id !== user?.user_id) {
              playDJNotification();
            }
          } else if (data.type === 'users') {
            setOnlineUsers(data.data);
          }
        };

        wsRef.current.onclose = () => {
          setConnected(false);
          // Reconnect after 3 seconds
          setTimeout(connectWS, 3000);
        };

        wsRef.current.onerror = () => {
          setConnected(false);
        };
      } catch (error) {
        console.log('WebSocket error:', error);
      }
    };

    connectWS();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, playDJNotification]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    try {
      const response = await fetch(`${API_URL}/api/chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: newMessage.trim() })
      });

      if (response.ok) {
        setNewMessage('');
        setShowEmojis(false);
      }
    } catch (error) {
      console.log('Failed to send message');
    }
  };

  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'dj':
        return <span className="ml-1 text-xs">🎤</span>;
      case 'mod':
        return <span className="ml-1 text-xs">🛡️</span>;
      case 'admin':
        return <span className="ml-1 text-xs">👑</span>;
      default:
        return null;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'dj':
        return 'var(--primary)';
      case 'mod':
        return '#22C55E';
      case 'admin':
        return '#F59E0B';
      default:
        return 'var(--text-main)';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div 
      className="glass rounded-2xl p-4 flex flex-col"
      style={{ height: '350px' }}
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" style={{ color: 'var(--primary)' }} />
          <h3 
            className="text-lg font-bold"
            style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
          >
            Live Chat
          </h3>
          {connected && (
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* DJ Active Indicator */}
          {djStatus && (
            <div 
              className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium animate-pulse"
              style={{ 
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                color: 'white'
              }}
              data-testid="dj-active-badge"
            >
              <Radio className="w-3 h-3" />
              <span>
                {djStatus.role === 'dj' ? 'DJ' : djStatus.role === 'admin' ? 'Admin' : 'Mod'} is here!
              </span>
            </div>
          )}
          
          {/* DJ Notification Toggle */}
          <button
            onClick={() => setDjNotifications(!djNotifications)}
            className="p-1.5 rounded-lg transition-colors"
            style={{ 
              backgroundColor: djNotifications ? 'var(--primary)' : 'var(--surface-highlight)',
              color: djNotifications ? 'white' : 'var(--text-muted)'
            }}
            title={djNotifications ? 'DJ notifications ON' : 'DJ notifications OFF'}
            data-testid="dj-notification-toggle"
          >
            {djNotifications ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          </button>
          
          <button
            onClick={() => setShowUsers(!showUsers)}
            className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs"
            style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-muted)' }}
          >
            <span className="relative flex items-center">
              <Users className="w-3 h-3" />
              {onlineUsers.length > 0 && (
                <span 
                  className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: '#22C55E' }}
                />
              )}
            </span>
            <span>{onlineUsers.length} Active</span>
          </button>
        </div>
      </div>

      {/* Online Users Panel */}
      {showUsers && onlineUsers.length > 0 && (
        <div 
          className="mb-3 p-3 rounded-xl text-xs"
          style={{ backgroundColor: 'var(--surface-highlight)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span 
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: '#22C55E' }}
            />
            <p style={{ color: 'var(--text-main)', fontWeight: '600' }}>
              {onlineUsers.length} Active Now
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {onlineUsers.map((u, i) => (
              <span 
                key={i} 
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
                style={{ backgroundColor: 'var(--surface)', color: getRoleColor(u.role) }}
              >
                <span 
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: '#22C55E' }}
                />
                {u.name}{getRoleBadge(u.role)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div 
        className="flex-1 overflow-y-auto space-y-2 mb-3 pr-1"
        style={{ minHeight: 0 }}
      >
        {messages.length === 0 ? (
          <p className="text-center text-sm py-8" style={{ color: 'var(--text-muted)' }}>
            No messages yet. Be the first!
          </p>
        ) : (
          messages.map((msg, index) => (
            <div 
              key={msg.message_id || index}
              className="flex gap-2 p-2 rounded-lg"
              style={{ 
                backgroundColor: msg.user_role !== 'user' ? `${getRoleColor(msg.user_role)}15` : 'transparent' 
              }}
            >
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: msg.user_role !== 'user' ? getRoleColor(msg.user_role) : 'var(--surface-highlight)' }}
              >
                {msg.user_role === 'dj' ? (
                  <Mic className="w-3 h-3 text-white" />
                ) : (
                  <User className="w-3 h-3" style={{ color: msg.user_role !== 'user' ? 'white' : 'var(--text-muted)' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span 
                    className="text-xs font-medium"
                    style={{ color: getRoleColor(msg.user_role) }}
                  >
                    {msg.user_name}
                    {getRoleBadge(msg.user_role)}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
                <p className="text-sm break-words" style={{ color: 'var(--text-main)' }}>
                  {msg.text}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {user ? (
        <div className="relative">
          {/* User info bar with logout */}
          <div 
            className="flex items-center justify-between mb-2 px-2 py-1.5 rounded-lg"
            style={{ backgroundColor: 'var(--surface-highlight)' }}
          >
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5" style={{ color: 'var(--primary)' }} />
              <span className="text-xs font-medium" style={{ color: 'var(--text-main)' }}>
                {user.name}
              </span>
              {user.role !== 'user' && (
                <span 
                  className="text-xs px-1.5 py-0.5 rounded capitalize"
                  style={{ 
                    backgroundColor: user.role === 'admin' ? '#FFD700' : user.role === 'dj' ? '#A855F7' : '#22C55E',
                    color: user.role === 'admin' ? 'black' : 'white',
                    fontSize: '10px'
                  }}
                >
                  {user.role}
                </span>
              )}
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors hover:bg-white/10"
              style={{ color: 'var(--text-muted)' }}
              data-testid="chat-logout-btn"
            >
              <LogOut className="w-3 h-3" />
              <span>Logout</span>
            </button>
          </div>

          {/* Emoji Picker */}
          {showEmojis && (
            <div 
              className="absolute bottom-full mb-2 left-0 p-2 rounded-lg flex flex-wrap gap-1"
              style={{ backgroundColor: 'var(--surface-highlight)' }}
            >
              {EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => addEmoji(emoji)}
                  className="w-8 h-8 rounded hover:bg-white/10 transition-colors text-lg"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSend} className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowEmojis(!showEmojis)}
              className="p-2 rounded-lg"
              style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-muted)' }}
            >
              <Smile className="w-5 h-5" />
            </button>
            
            <input
              type="text"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="flex-1 p-2 rounded-lg text-sm outline-none"
              style={{ 
                backgroundColor: 'var(--surface-highlight)',
                color: 'var(--text-main)'
              }}
              data-testid="chat-message-input"
            />
            
            <button
              type="submit"
              disabled={!newMessage.trim()}
              className="p-2 rounded-lg disabled:opacity-50"
              style={{ backgroundColor: 'var(--primary)', color: 'white' }}
              data-testid="chat-send-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      ) : (
        <button
          onClick={() => navigate('/login')}
          className="w-full flex items-center justify-center gap-2 p-3 rounded-xl transition-all hover:scale-105"
          style={{ 
            background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
            color: 'white'
          }}
          data-testid="chat-login-btn"
        >
          <LogIn className="w-4 h-4" />
          <span className="font-medium">Sign in to chat</span>
        </button>
      )}
    </div>
  );
}
